import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Search, UserCircle } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { apiClient } from '@/services/api-client';
import type { User, UserStoryCardMapping, StoryCardMappingNode } from '@/types';

interface MapStoryCardsDialogProps {
  user: User | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

function filterTree(nodes: StoryCardMappingNode[], query: string): StoryCardMappingNode[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return nodes;

  return nodes
    .map((node) => {
      const children = filterTree(node.children, normalized);
      const matches =
        node.title.toLowerCase().includes(normalized) ||
        node.cardId.toLowerCase().includes(normalized) ||
        (node.prompt || '').toLowerCase().includes(normalized);

      if (!matches && children.length === 0) {
        return null;
      }

      return { ...node, children };
    })
    .filter((node): node is StoryCardMappingNode => node !== null);
}

export function MapStoryCardsDialog({
  user,
  open,
  onOpenChange,
  onSuccess,
}: MapStoryCardsDialogProps) {
  const queryClient = useQueryClient();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [initialSelectedIds, setInitialSelectedIds] = useState<string[]>([]);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const [sectionSearch, setSectionSearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<UserStoryCardMapping, Error>({
    queryKey: ['admin', 'users', user?.id, 'story-cards'],
    enabled: open && !!user?.id,
    staleTime: 0,
    queryFn: async () => {
      const response = await apiClient.get<UserStoryCardMapping>(
        `/api/v1/admin/users/${user!.id}/story-cards`
      );
      if (!response.success || !response.data) {
        throw new Error(response.error?.message ?? 'Failed to load story card mapping');
      }
      return response.data;
    },
  });

  useEffect(() => {
    if (!open) {
      setSectionSearch('');
      setSaveError(null);
      return;
    }

    if (data) {
      const directIds = [...data.existingAssignments.userOverrideStoryCardIds].sort();
      setSelectedIds(directIds);
      setInitialSelectedIds(directIds);
      setSaveError(null);
    }
  }, [data, open]);

  useEffect(() => {
    if (!data) {
      setActiveSectionId(null);
      return;
    }

    const firstSection = data.groups.flatMap((group) => group.cards)[0];
    const firstSectionId = firstSection?.id ?? null;
    setActiveSectionId((current) => {
      if (current && data.groups.some((group) => group.cards.some((card) => card.id === current))) {
        return current;
      }
      return firstSectionId;
    });
  }, [data]);

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const initialSelectedKey = initialSelectedIds.join('|');
  const selectedKey = [...selectedIds].sort().join('|');
  const hasChanges = initialSelectedKey !== selectedKey;

  const allNodesById = useMemo(() => {
    const map = new Map<string, StoryCardMappingNode>();
    const walk = (nodes: StoryCardMappingNode[]) => {
      for (const node of nodes) {
        map.set(node.id, node);
        walk(node.children);
      }
    };
    data?.groups.forEach((group) => walk(group.cards));
    return map;
  }, [data]);

  const personaIdSet = useMemo(
    () => new Set(data?.existingAssignments.personaStoryCardIds ?? []),
    [data]
  );

  const countNodes = (node: StoryCardMappingNode): number =>
    1 + node.children.reduce((sum, child) => sum + countNodes(child), 0);

  const countSelectedNodes = (node: StoryCardMappingNode): number =>
    (selectedIdSet.has(node.id) ? 1 : 0) +
    node.children.reduce((sum, child) => sum + countSelectedNodes(child), 0);

  const countPersonaNodes = (node: StoryCardMappingNode): number =>
    (personaIdSet.has(node.id) ? 1 : 0) +
    node.children.reduce((sum, child) => sum + countPersonaNodes(child), 0);

  const mappingSections = useMemo(
    () =>
      (data?.groups ?? []).flatMap((group) =>
        group.cards.map((card) => ({
          id: card.id,
          groupId: group.groupId,
          groupName: group.groupName,
          node: card,
          totalCount: countNodes(card),
          directCount: countSelectedNodes(card),
          personaCount: countPersonaNodes(card),
        }))
      ),
    [data, personaIdSet, selectedIdSet]
  );

  const activeSection = useMemo(
    () =>
      mappingSections.find((section) => section.id === activeSectionId) ??
      mappingSections[0] ??
      null,
    [activeSectionId, mappingSections]
  );

  const filteredSectionNode = useMemo(() => {
    if (!activeSection) return null;
    if (!sectionSearch.trim()) return activeSection.node;
    return filterTree([activeSection.node], sectionSearch)[0] ?? null;
  }, [activeSection, sectionSearch]);

  const getAncestorIds = (cardId: string): string[] => {
    const ancestorIds: string[] = [];
    let current = allNodesById.get(cardId);

    while (current?.parentCardId) {
      ancestorIds.push(current.parentCardId);
      current = allNodesById.get(current.parentCardId);
    }

    return ancestorIds;
  };

  const getDescendantIds = (cardId: string): string[] => {
    const node = allNodesById.get(cardId);
    if (!node) return [];

    return node.children.flatMap((child) => [child.id, ...getDescendantIds(child.id)]);
  };

  const toggleCard = (node: StoryCardMappingNode, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(node.id);
        for (const ancestorId of getAncestorIds(node.id)) {
          next.add(ancestorId);
        }
      } else {
        for (const id of [node.id, ...getDescendantIds(node.id)]) {
          next.delete(id);
        }
      }
      return Array.from(next).sort();
    });
  };

  const getStatus = (cardId: string) => {
    const inherited = personaIdSet.has(cardId);
    const selected = selectedIdSet.has(cardId);

    if (selected && inherited) {
      return [
        { label: 'Mapped to User', variant: 'default' as const },
        { label: 'Inherited via Persona', variant: 'secondary' as const },
      ];
    }
    if (selected && !inherited) {
      return [{ label: 'Mapped to User', variant: 'default' as const }];
    }
    if (inherited) {
      return [{ label: 'Inherited via Persona', variant: 'secondary' as const }];
    }
    return [{ label: 'Available', variant: 'outline' as const }];
  };

  const directCount = selectedIds.length;
  const personaCount = personaIdSet.size;
  const totalRootCards = mappingSections.length;

  const setSectionSelection = (node: StoryCardMappingNode, checked: boolean) => {
    const ids = [node.id, ...getDescendantIds(node.id)];
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        ids.forEach((id) => next.add(id));
      } else {
        ids.forEach((id) => next.delete(id));
      }
      return Array.from(next).sort();
    });
  };

  const handleSave = async () => {
    if (!user) return;

    setIsSaving(true);
    setSaveError(null);
    try {
      const response = await apiClient.put<UserStoryCardMapping>(
        `/api/v1/admin/users/${user.id}/story-cards`,
        { storyCardIds: selectedIds }
      );

      if (!response.success) {
        setSaveError(response.error?.message ?? 'Failed to save story card mapping');
        return;
      }

      await queryClient.invalidateQueries({ queryKey: ['admin', 'users', user.id, 'story-cards'] });
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      onOpenChange(false);
      onSuccess?.();
    } catch {
      setSaveError('Failed to save story card mapping');
    } finally {
      setIsSaving(false);
    }
  };

  if (!user) return null;

  const displayName =
    user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.firstName || user.email;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[94vh] max-w-[94vw] flex-col overflow-hidden p-0 xl:max-w-7xl">
        <div className="shrink-0 border-b px-5 pb-4 pt-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <DialogHeader className="text-left">
              <DialogTitle className="font-display text-xl">Map Story Cards</DialogTitle>
              <DialogDescription>
                Choose story cards section by section and map only the cards this user should see.
              </DialogDescription>
            </DialogHeader>

            {data && !isLoading && (
              <div className="min-w-0 xl:max-w-[60%]">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="truncate font-medium text-foreground">{displayName}</p>
                  <Badge variant="outline">{user.role}</Badge>
                  <Badge variant={data.primaryPersona ? 'secondary' : 'outline'}>
                    <UserCircle className="mr-1 h-3 w-3" />
                    {data.primaryPersona
                      ? `${data.primaryPersona.name} (${data.primaryPersona.code})`
                      : 'No persona assigned'}
                  </Badge>
                </div>
                <p className="mt-1 truncate text-sm text-muted-foreground">{user.email}</p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <CompactStat label="Direct" value={directCount} />
                  <CompactStat label="Persona" value={personaCount} />
                  <CompactStat label="Sections" value={totalRootCards} />
                </div>
              </div>
            )}
          </div>
        </div>

        {isLoading && (
          <div className="flex min-h-[18rem] items-center justify-center px-6 py-6 text-muted-foreground">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Loading story card mapping...
          </div>
        )}

        {error && (
          <div className="mx-6 mt-6 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {error.message}
          </div>
        )}

        {data && !isLoading && (
          <div className="flex min-h-0 flex-1 flex-col px-5 py-4">
            <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[280px,minmax(0,1fr)]">
              <aside className="min-h-0 rounded-lg border bg-muted/10">
                <div className="border-b px-4 py-3">
                  <p className="text-sm font-medium text-foreground">Top-Level Story Cards</p>
                  <p className="text-xs text-muted-foreground">
                    Pick a section to map its child story cards.
                  </p>
                </div>
                <div className="max-h-full space-y-2 overflow-y-auto p-3">
                  {mappingSections.map((section) => {
                    const isActive = section.id === activeSection?.id;
                    return (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => {
                          setActiveSectionId(section.id);
                          setSectionSearch('');
                        }}
                        className={cn(
                          'w-full rounded-lg border px-3 py-3 text-left transition-colors',
                          isActive
                            ? 'border-orb-gold/50 bg-orb-gold/10'
                            : 'border-border bg-background hover:bg-muted/40'
                        )}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-foreground">
                              {section.node.title}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {section.groupName}
                            </p>
                          </div>
                          <Badge variant={isActive ? 'default' : 'outline'} className="shrink-0">
                            {section.directCount}
                          </Badge>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                          <span>{section.totalCount} cards</span>
                          <span>{section.personaCount} persona</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </aside>

              <section className="min-h-0 rounded-lg border">
                {!activeSection ? (
                  <div className="flex h-full items-center justify-center p-6 text-sm text-muted-foreground">
                    No story card sections available.
                  </div>
                ) : (
                  <div className="flex h-full min-h-0 flex-col">
                    <div className="shrink-0 border-b px-4 py-3">
                      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <p className="text-base font-semibold text-foreground">
                              {activeSection.node.title}
                            </p>
                            <Badge variant="outline" className="font-mono text-[10px]">
                              {activeSection.node.cardId}
                            </Badge>
                            <Badge variant="secondary" className="text-[10px] uppercase">
                              {activeSection.groupName}
                            </Badge>
                            {activeSection.node.prompt && (
                              <span className="text-sm text-muted-foreground">
                                {activeSection.node.prompt}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setSectionSelection(activeSection.node, false)}
                          >
                            Clear Section
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setSectionSelection(activeSection.node, true)}
                          >
                            Select Section
                          </Button>
                          <CompactStat
                            label="Total cards"
                            value={activeSection.totalCount}
                            compact
                          />
                        </div>
                      </div>

                      <div className="relative mt-3 max-w-xl">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          placeholder="Search within this section..."
                          value={sectionSearch}
                          onChange={(e) => setSectionSearch(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    <div className="min-h-0 flex-1 overflow-y-auto p-4 pr-3">
                      {!filteredSectionNode ? (
                        <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                          No story cards in this section match your search.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <StoryCardTreeItem
                            node={filteredSectionNode}
                            level={0}
                            selectedIdSet={selectedIdSet}
                            onToggle={toggleCard}
                            getStatus={getStatus}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </section>
            </div>

            {saveError && <p className="mt-4 text-sm text-destructive">{saveError}</p>}
          </div>
        )}

        <DialogFooter className="shrink-0 border-t px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !data || !hasChanges}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Mapping
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompactStat({
  label,
  value,
  compact = false,
}: {
  label: string;
  value: number;
  compact?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-md border bg-background/80 px-2.5 py-1.5',
        compact ? 'min-w-[110px]' : 'min-w-[88px]'
      )}
    >
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="text-sm font-semibold text-foreground">{value}</p>
    </div>
  );
}

function StoryCardTreeItem({
  node,
  level,
  selectedIdSet,
  onToggle,
  getStatus,
}: {
  node: StoryCardMappingNode;
  level: number;
  selectedIdSet: Set<string>;
  onToggle: (node: StoryCardMappingNode, checked: boolean) => void;
  getStatus: (
    cardId: string
  ) => Array<{ label: string; variant: 'default' | 'secondary' | 'outline' }>;
}) {
  const statuses = getStatus(node.id);
  const isChecked = selectedIdSet.has(node.id);

  return (
    <div className="space-y-3">
      <label
        className="flex items-start gap-3 rounded-md border p-3"
        style={{ marginLeft: `${level * 18}px` }}
      >
        <input
          type="checkbox"
          checked={isChecked}
          onChange={(e) => onToggle(node, e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-gray-300"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium text-foreground">{node.title}</p>
            <Badge variant="outline" className="font-mono text-[10px]">
              {node.cardId}
            </Badge>
            {statuses.map((status) => (
              <Badge key={status.label} variant={status.variant} className="text-[10px] uppercase">
                {status.label}
              </Badge>
            ))}
          </div>
          {node.prompt && <p className="mt-1 text-sm text-muted-foreground">{node.prompt}</p>}
        </div>
      </label>

      {node.children.map((child) => (
        <StoryCardTreeItem
          key={child.id}
          node={child}
          level={level + 1}
          selectedIdSet={selectedIdSet}
          onToggle={onToggle}
          getStatus={getStatus}
        />
      ))}
    </div>
  );
}
